import { memo, useState, useCallback } from "react";
import { useI18n } from "@/i18n";
import { useSessionStore } from "@/application/stores/sessionStore";
import { switchApp, isAuthError } from "@/infrastructure/auth/authService";
import { typography, transitions } from "@/design-system/tokens";
import { CONFIG } from "@/config";

const APP_CONFIG: Record<string, { name: string; url: string; logo: "nexbot" | "warehouse" | "tracking" }> = {
  NEXBOT: { name: "NexBot", url: CONFIG.NEXBOT_URL, logo: "nexbot" },
  NEXO_WAREHOUSE: { name: "Nexo Warehouse", url: CONFIG.WAREHOUSE_URL, logo: "warehouse" },
  NEXO_TRACKING: { name: "Nexo Tracking", url: CONFIG.TRACKING_URL, logo: "tracking" },
};

const CURRENT_APP_ID = "NEXO_WAREHOUSE";

function getAppUrl(appId: string): string {
  return APP_CONFIG[appId]?.url ?? "#";
}

export const AppSwitcher = memo(function AppSwitcher({ collapsed }: { collapsed: boolean }) {
  const { t } = useI18n();
  const appIds = useSessionStore(s => s.appIds) ?? [];
  const [switching, setSwitching] = useState(false);
  const [blockedApp, setBlockedApp] = useState<string | null>(null);

  const otherApps = appIds.filter(id => id !== CURRENT_APP_ID);

  const handleSwitch = useCallback(async (appId: string) => {
    setSwitching(true);
    setBlockedApp(null);
    try {
      const newToken = await switchApp(appId);
      const url = getAppUrl(appId);
      window.location.href = `${url}?token=${encodeURIComponent(newToken)}`;
    } catch (err) {
      if (isAuthError(err) && err.message === "SUBSCRIPTION_EXPIRED") {
        setBlockedApp(appId);
      }
      setSwitching(false);
    }
  }, []);

  if (otherApps.length === 0) return null;

  return (
    <>
      <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
        {!collapsed && (
          <span style={{
            fontFamily: typography.fontFamily,
            fontSize: "9px",
            fontWeight: typography.weight.bold,
            color: "rgba(255,255,255,0.2)",
            textTransform: "uppercase",
            letterSpacing: "0.12em",
            padding: "0 4px",
          }}>
            {t.platform.switchTo}
          </span>
        )}
        {otherApps.map(appId => {
          const cfg = APP_CONFIG[appId];
          if (!cfg) return null;
          return (
            <button
              key={appId}
              onClick={() => handleSwitch(appId)}
              disabled={switching}
              title={cfg.name}
              style={{
                width: "100%",
                display: "flex",
                alignItems: "center",
                gap: collapsed ? "0" : "10px",
                justifyContent: collapsed ? "center" : "flex-start",
                padding: collapsed ? "8px 0" : "9px 12px",
                background: "rgba(255,255,255,0.04)",
                border: "1px solid rgba(255,255,255,0.06)",
                borderRadius: "10px",
                cursor: switching ? "wait" : "pointer",
                opacity: switching ? 0.5 : 1,
                transition: `all ${transitions.fast}`,
              }}
              onMouseEnter={e => {
                const el = e.currentTarget as HTMLButtonElement;
                el.style.background = "rgba(255,255,255,0.08)";
                el.style.borderColor = "rgba(255,255,255,0.12)";
              }}
              onMouseLeave={e => {
                const el = e.currentTarget as HTMLButtonElement;
                el.style.background = "rgba(255,255,255,0.04)";
                el.style.borderColor = "rgba(255,255,255,0.06)";
              }}
            >
              <AppLogo type={cfg.logo} />
              {!collapsed && (
                <span style={{
                  fontFamily: typography.fontFamily,
                  fontSize: "12px",
                  fontWeight: typography.weight.medium,
                  color: "rgba(255,255,255,0.6)",
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}>
                  {cfg.name}
                </span>
              )}
            </button>
          );
        })}
      </div>

      {blockedApp && (
        <div
          onClick={() => setBlockedApp(null)}
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,0.5)",
            backdropFilter: "blur(4px)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 9999,
            padding: "24px",
          }}
        >
          <div
            onClick={e => e.stopPropagation()}
            style={{
              background: "#fff",
              borderRadius: "20px",
              padding: "40px 36px",
              width: "100%",
              maxWidth: "400px",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: "20px",
              textAlign: "center",
            }}
          >
            <AppLogo type={APP_CONFIG[blockedApp]?.logo ?? "warehouse"} />
            <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
              <h2 style={{ fontFamily: typography.fontFamily, fontSize: "1.25rem", fontWeight: typography.weight.bold, color: "#111", margin: 0 }}>
                {t.login.subscriptionTitle}
              </h2>
              <p style={{ fontFamily: typography.fontFamily, fontSize: typography.size.sm, color: "#666", margin: 0, lineHeight: "1.5" }}>
                {t.login.subscriptionMessage}
              </p>
            </div>
          </div>
        </div>
      )}
    </>
  );
});

function AppLogo({ type }: { type: "nexbot" | "warehouse" | "tracking" }) {
  if (type === "nexbot") {
    return (
      <div style={{
        width: "24px", height: "24px", background: "#111", borderRadius: "7px",
        display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
      }}>
        <span style={{ fontFamily: typography.fontFamily, fontSize: "13px", fontWeight: 800, color: "#f5c518", lineHeight: 1 }}>N</span>
      </div>
    );
  }

  if (type === "tracking") {
    return (
      <div style={{
        width: "24px", height: "24px", background: "#111", borderRadius: "50%",
        display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
      }}>
        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M12 2 2 7l10 5 10-5-10-5Z" />
          <path d="m2 17 10 5 10-5" />
          <path d="m2 12 10 5 10-5" />
        </svg>
      </div>
    );
  }

  return (
    <div style={{
      width: "24px", height: "24px", background: "#EB5139", borderRadius: "7px",
      display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
    }}>
      <svg width="13" height="13" viewBox="0 0 20 20" fill="none">
        <path d="M3 7L10 3L17 7V13L10 17L3 13V7Z" stroke="white" strokeWidth="1.8" strokeLinejoin="round" />
      </svg>
    </div>
  );
}

function CopyLinkButton({ url }: { url: string }) {
  const { t } = useI18n();
  const [copied, setCopied] = useState(false);

  const handleCopy = useCallback(() => {
    navigator.clipboard.writeText(url).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  }, [url]);

  return (
    <button
      onClick={handleCopy}
      style={{
        fontFamily: typography.fontFamily,
        fontSize: typography.size.sm,
        fontWeight: typography.weight.semibold,
        color: copied ? "#16a34a" : "#555",
        background: "transparent",
        border: `1px solid ${copied ? "#bbf7d0" : "#e0e0e0"}`,
        borderRadius: "10px",
        padding: "10px 20px",
        cursor: "pointer",
        display: "flex",
        alignItems: "center",
        gap: "8px",
        transition: `all ${transitions.fast}`,
      }}
    >
      {copied ? (
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#16a34a" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12" /></svg>
      ) : (
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="9" y="9" width="13" height="13" rx="2" /><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" /></svg>
      )}
      {copied ? (t.login.subscriptionCopied ?? "Copiado") : (t.login.subscriptionCopyLink ?? "Copiar enlace")}
    </button>
  );
}
